/*
Reusable delete dialog component to keep our codebase DRY and consistent w/ ws-common-base-ui. 

Props
- title: string
- message: string
- onDelete: React.MouseEventHandler<HTMLButtonElement>
- onClose: () => void - called when the user clicks the close button or cancels the dialog
- isOpen: boolean
- isLoading?: boolean (for delete button)

Usage:
<DeleteDialog
  title="Delete Contact"
  message="Are you sure you want to delete this contact?"
  onDelete={() => {}}
  onClose={() => {}}
  isOpen={true}
  isLoading?={false}
/>
*/

import { Button, DeleteIcon, Dialog } from '@wonderschool/common-base-ui';
import { useTranslation } from 'react-i18next';

interface DeleteDialogProps {
  title: string;
  message?: string | React.ReactNode;
  onDelete: React.MouseEventHandler<HTMLButtonElement>;
  onClose: () => void;
  isOpen: boolean;
  isLoading?: boolean;
}

export default function DeleteDialog({
  title,
  message,
  onDelete,
  onClose,
  isOpen,
  isLoading = false,
}: DeleteDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog isOpen={isOpen} onClose={onClose} hasCloseButton={true} fullScreenOnMobile={true}>
      {/* Title + message content in panel */}
      <Dialog.Panel>
        <Dialog.Title data-testid="delete-dialog-title">{title}</Dialog.Title>
        <Dialog.Description data-testid="delete-dialog-message">{message}</Dialog.Description>
      </Dialog.Panel>
      {/* Cancel + Delete buttons in actions. Cancel secondary on left, bottom on mobile; Delete primary on right, top on mobile. */}
      <Dialog.Actions>
        <div className="grid w-full grid-cols-1 items-center gap-3 sm:grid-cols-2">
          <Button onClick={onClose} label={t('common.cancel')} extraClasses="w-full order-2 sm:order-1" />
          <Button
            loading={isLoading}
            onClick={onDelete}
            label={t('common.delete')}
            primary
            preIcon={<DeleteIcon className="size-4" />}
            extraClasses="w-full bg-red-500 hover:bg-red-600 order-1 sm:order-2"
          />
        </div>
      </Dialog.Actions>
    </Dialog>
  );
}
